import { useSyncExternalStore } from "react";
import type { EventSubtask } from "../../../shared/types";
import { getToday } from "../../../shared/types/date-helpers";

const STORAGE_KEY = "palnia_daily_checks";

interface DailyChecksStore {
  date: string;
  // normalizedTitle → subtaskId → boolean
  checks: Record<string, Record<string, boolean>>;
}

// Normalise le titre pour éviter les problèmes de casse / espaces
function normalizeTitle(title: string): string {
  return title.toLowerCase().trim();
}

function loadChecks(): DailyChecksStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { date: getToday(), checks: {} };
    const parsed = JSON.parse(raw) as DailyChecksStore;
    if (parsed.date !== getToday()) return { date: getToday(), checks: {} };
    return parsed;
  } catch {
    return { date: getToday(), checks: {} };
  }
}

let checksCache: DailyChecksStore = loadChecks();
const listeners = new Set<() => void>();

function emitChange(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(checksCache));
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

function ensureTodayCache(): void {
  const today = getToday();
  if (checksCache.date !== today) {
    checksCache = { date: today, checks: {} };
  }
}

// Reset automatique si l'app reste ouverte après minuit
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      const today = getToday();
      if (checksCache.date !== today) {
        checksCache = { date: today, checks: {} };
        emitChange();
      }
    }
  });
}

function toggleCheck(eventTitle: string, subtaskId: string): void {
  ensureTodayCache();
  const key = normalizeTitle(eventTitle);
  const titleChecks = checksCache.checks[key] ?? {};
  checksCache = {
    ...checksCache,
    checks: {
      ...checksCache.checks,
      [key]: { ...titleChecks, [subtaskId]: !titleChecks[subtaskId] },
    },
  };
  emitChange();
}

function resetChecks(eventTitle: string): void {
  ensureTodayCache();
  const key = normalizeTitle(eventTitle);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [key]: _removed, ...rest } = checksCache.checks;
  checksCache = { ...checksCache, checks: rest };
  emitChange();
}

function isChecked(eventTitle: string, subtaskId: string): boolean {
  ensureTodayCache();
  return checksCache.checks[normalizeTitle(eventTitle)]?.[subtaskId] ?? false;
}

function getCheckedCount(
  eventTitle: string,
  subtasks: readonly EventSubtask[],
): number {
  ensureTodayCache();
  const key = normalizeTitle(eventTitle);
  const titleChecks = checksCache.checks[key] ?? {};
  return subtasks.filter((s) => titleChecks[s.id]).length;
}

function areAllChecked(
  eventTitle: string,
  subtasks: readonly EventSubtask[],
): boolean {
  if (subtasks.length === 0) return false;
  return getCheckedCount(eventTitle, subtasks) === subtasks.length;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  function handleStorageChange(event: StorageEvent): void {
    if (event.key === STORAGE_KEY) {
      checksCache = loadChecks();
      for (const l of listeners) l();
    }
  }

  window.addEventListener("storage", handleStorageChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorageChange);
  };
}

function getSnapshot(): DailyChecksStore {
  return checksCache;
}

export function useDailyChecks() {
  useSyncExternalStore(subscribe, getSnapshot);

  return {
    isChecked,
    toggleCheck,
    areAllChecked,
    getCheckedCount,
    resetChecks,
  };
}
